/**
 * Kaching purchase-card UI with live Shopify product data. Existing automatic
 * discounts own cart pricing. Native Teeinblue submission saves one design at
 * a time; no personalization properties or cart requests are cloned/replaced.
 */
const FORM = 'product-form-component[data-product-id="15440042524971"]';
let componentPromise;

class ApparelBundlePilot extends HTMLElement {
  listeners = null;
  widget = null;
  units = [];
  active = 0;
  variants = [];
  designs = new Map();
  switching = false;

  connectedCallback() {
    if (this.listeners) return;
    this.listeners = new AbortController();
    this.addEventListener('click', this.onDesignClick, { signal: this.listeners.signal });
    // Also covers the Teeinblue preview modal's own native add-to-cart button.
    document.addEventListener('click', this.ensureSingleDesign, {
      capture: true, signal: this.listeners.signal,
    });
    this.mount().catch((error) => {
      console.warn('[Nuzzle purchase cards]', error);
      this.restoreNative();
    });
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.listeners = null;
    this.restoreNative();
  }

  async mount() {
    const signal = this.listeners.signal;
    const response = await fetch(this.dataset.configUrl, { signal });
    if (!response.ok) throw new Error('Purchase-card configuration unavailable');
    const attrs = await response.json();
    componentPromise ??= import(this.dataset.componentUrl);
    await componentPromise;
    if (signal.aborted || !this.isConnected) return;

    const source = JSON.parse(this.dataset.productJson);
    if (source.id !== 15440042524971 || !Array.isArray(source.variants)) throw new Error('Invalid product context');
    const image = source.featured_image?.src ?? source.featured_image ?? null;
    const product = {
      id: source.id,
      handle: source.handle,
      title: source.title,
      url: `/products/${source.handle}`,
      availableForSale: source.available,
      image,
      collectionIds: [],
      metafields: { text: null, text2: null, text3: null, text4: null },
      options: source.options.map((name, index) => ({
        name, defaultName: name, position: index + 1,
        optionValues: [...new Set(source.variants.map((variant) => variant.options[index]))]
          .map((value) => ({ id: `${index}:${value}`, name: value, defaultName: value, swatch: { color: null, image: null } })),
      })),
      variants: source.variants.map((variant) => ({
        id: variant.id,
        availableForSale: variant.available,
        price: variant.price,
        compareAtPrice: variant.compare_at_price,
        options: variant.options,
        imageId: variant.featured_image?.id ?? null,
        image: variant.featured_image?.src ?? image,
        sellingPlans: [],
        inventoryManagement: variant.inventory_management ?? null,
        inventoryPolicy: null,
        inventoryQuantity: null,
        unitPriceMeasurement: null,
      })),
      requiresSellingPlan: source.requires_selling_plan ?? false,
      sellingPlans: [],
      isNativeBundle: false,
    };
    this.variants = product.variants;
    const variantId = Number(this.dataset.currentVariant);
    if (!this.variants.some((variant) => variant.id === variantId)) throw new Error('Invalid selected variant');
    product.selectedVariantId = variantId;
    attrs.product = JSON.stringify(product);
    attrs['current-variant-id'] = String(variantId);
    const config = JSON.parse(attrs.config);
    config.country = this.dataset.country;
    config.locale = this.dataset.locale;
    config.moneyFormat = this.dataset.moneyFormat;
    attrs.config = JSON.stringify(config);
    this.units = [variantId];

    this.widget = document.createElement('kaching-bundles-block');
    for (const [name, value] of Object.entries(attrs)) this.widget.setAttribute(name, value);
    this.widget.addEventListener('variants-changed', this.onVariantsChanged, { signal });
    this.querySelector('[data-bundle-widget]').replaceChildren(this.widget);
    // Component styles and event handling are the vendor's own module.
    document.body.classList.add('np-kaching-pilot-ready');
    this.querySelector('[data-bundle-terms]').hidden = false;
    this.renderDesigns();
  }

  onVariantsChanged = (event) => {
    const units = [];
    for (const item of event.detail?.variantIdQuantities ?? []) {
      const id = Number(item.variantId);
      if (!this.variants.some((variant) => variant.id === id)) return;
      for (let count = 0; count < Number(item.quantity); count++) units.push(id);
    }
    if (units.length < 1 || units.length > 2) return;
    this.units = units;
    this.active = Math.min(this.active, units.length - 1);
    this.renderDesigns();
    this.syncNativeVariant();
  };

  renderDesigns() {
    const controls = this.querySelector('[data-designs]');
    controls.replaceChildren();
    controls.hidden = this.units.length < 2;
    this.querySelector('[data-bundle-hint]').hidden = this.units.length < 2;
    this.units.forEach((id, index) => {
      const variant = this.variants.find((item) => item.id === id);
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.designIndex = String(index);
      button.setAttribute('aria-pressed', String(this.active === index));
      button.textContent = `${this.dataset.actionLabel} #${index + 1} · ${variant.options.join(' / ')}`;
      controls.append(button);
    });
    this.ensureSingleDesign();
  }

  onDesignClick = async (event) => {
    const button = event.target instanceof Element && event.target.closest('[data-design-index]');
    if (!button || !this.contains(button) || this.switching) return;
    const index = Number(button.dataset.designIndex);
    if (index === this.active) return;
    const current = this.captureDesign();
    if (!current) return;
    this.designs.set(this.active, current);
    const next = this.designs.get(index) ?? { ...current, texts: current.texts.map(([id]) => [id, '']) };
    this.active = index;
    this.switching = true;
    this.renderDesigns();
    this.syncNativeVariant();
    try {
      await this.restoreDesign(next);
      document.querySelector(`${FORM} #tee-artwork-form`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } finally {
      this.switching = false;
    }
  };

  captureDesign() {
    const form = document.querySelector(`${FORM} #tee-artwork-form`);
    if (!form) return null;
    return {
      selected: Array.from(form.querySelectorAll('.tee-radio.active input, .tee-clipart.active input'))
        .map((input) => input.id),
      texts: Array.from(form.querySelectorAll('input[type="text"],textarea'))
        .map((input) => [input.id, input.value]),
    };
  }

  async restoreDesign(design) {
    // Teeinblue owns validation and sync between kid-count templates. Restore the
    // template through its own label before restoring the template's child fields.
    const tick = () => new Promise((resolve) => requestAnimationFrame(resolve));
    const templateId = design.selected.find((id) => id.startsWith('1369378-template-'));
    if (templateId) this.activateNativeOption(templateId);
    await tick();
    for (const id of design.selected) {
      if (id !== templateId) this.activateNativeOption(id);
    }
    await tick();
    for (const [id, value] of design.texts) {
      const input = document.getElementById(id);
      if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) || !input.closest(`${FORM} #tee-artwork-form`)) continue;
      input.value = value;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  activateNativeOption(id) {
    const input = document.getElementById(id);
    if (!(input instanceof HTMLInputElement) || !input.closest(`${FORM} #tee-artwork-form`)) return;
    const option = input.closest('.tee-radio,.tee-clipart');
    if (!option?.classList.contains('active')) option?.querySelector('label')?.click();
  }

  syncNativeVariant() {
    const id = this.units[this.active];
    const input = document.querySelector(`.variant-picker input[data-variant-id="${id}"]`);
    if (input instanceof HTMLInputElement && !input.checked && !input.disabled) input.click();
  }

  ensureSingleDesign = (event) => {
    if (event) {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest(`${FORM} button[type="submit"], #preview-teeAtcButton`)) return;
    }
    const input = document.querySelector(`${FORM} quantity-selector-component input[name="quantity"]`);
    if (!(input instanceof HTMLInputElement)) return;
    const quantity = input.closest('quantity-selector-component');
    if (input.value !== '1' && typeof quantity?.setValue === 'function') {
      quantity.setValue('1');
      quantity.updateButtonStates();
      quantity.onQuantityChange();
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  restoreNative() {
    document.body.classList.remove('np-kaching-pilot-ready');
  }
}

if (!customElements.get('np-apparel-bundle-pilot')) {
  customElements.define('np-apparel-bundle-pilot', ApparelBundlePilot);
}
